import { showAddOrEditCouncilState } from '@/atoms';
import CustomFileInput from '@/components/common/inputs/CustomFileInput';
import { EditCouncilDTO, getCouncilProfileUrl, useCreateCouncil, useEditCouncil } from '@/services/councils';
import { Button, Textarea } from '@mantine/core';
import { notifications } from "@mantine/notifications";
import { useQueryClient } from '@tanstack/react-query';
import { ModalLayout, TextInput } from 'moh-ui';
import { useState } from 'react';
import { useRecoilState } from 'recoil';

export default function AddUpdateCouncil() {
    const [show, setShow] = useRecoilState(showAddOrEditCouncilState);
    const {
        mutate: createCouncil,
        isPending: creatingCouncil
    } = useCreateCouncil();
    const {
        mutate: editCouncil,
        isPending: editingCouncil
    } = useEditCouncil();
    const queryClient = useQueryClient();
    const [newCouncilProfileDocument, setNewCouncilProfileDocument] = useState<File | null>(null);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget);

        const data = {
            councilName: formData.get('councilName') as string,
            councilCode: formData.get('councilCode') as string,
            councilProfileDocument: newCouncilProfileDocument,
            address: formData.get('address') as string,
            telephone: formData.get('telephone') as string,
            website: formData.get('website') as string,
            emails: (formData.get('emails') as string).split(',').map(email => email.trim()),
            registrarName: formData.get('registrarName') as string,
        };

        if (show?.action === 'add') {

            if (!data.councilProfileDocument) {
                notifications.show({
                    title: "Error",
                    message: "Please upload a council profile document.",
                    color: "red"
                });
                return;
            }


            createCouncil({
                ...data,
                councilProfileDocument: data.councilProfileDocument
            }, {
                onSuccess: () => {
                    notifications.show({
                        title: "Success",
                        message: "Council created successfully",
                        color: "green"
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['councils']
                    });
                    setNewCouncilProfileDocument(null);
                    setShow(null);
                },
                onError: (error) => {
                    notifications.show({
                        title: "Error",
                        message: error.message || "Failed to create council",
                        color: "red"
                    });
                }
            });
        } else if (show?.action === 'edit' && show.council) {
            const updatedData: Omit<EditCouncilDTO, "id"> = {};
            if (data.councilName !== show.council.councilName) updatedData.councilName = data.councilName;
            if (data.councilCode !== show.council.councilCode) updatedData.councilCode = data.councilCode;
            if (newCouncilProfileDocument) {
                updatedData.councilProfileDocument = newCouncilProfileDocument;
            }
            if (JSON.stringify(data.emails) !== JSON.stringify(show.council.emails)) {
                updatedData.emails = data.emails
            }
            if (data.address !== show.council.address) updatedData.address = data.address;
            if (data.telephone !== show.council.telephone) updatedData.telephone = data.telephone;
            if (data.website !== show.council.website) updatedData.website = data.website;
            if (data.registrarName !== (show.council.registrarName || "")) updatedData.registrarName = data.registrarName;

            editCouncil({ ...updatedData, id: show.council.id }, {
                onSuccess: () => {
                    notifications.show({
                        title: "Success",
                        message: "Council updated successfully",
                        color: "green"
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['councils']
                    });
                    setNewCouncilProfileDocument(null);
                    setShow(null);
                },
                onError: (error) => {
                    notifications.show({
                        title: "Error",
                        message: error.message || "Failed to update council",
                        color: "red"
                    });
                }
            });
        }
    };

    return (
        <ModalLayout
            open={Boolean(show?.show)}
            onClose={() => setShow(null)}
            size={600}
        >
            <div className='mb-5'>
                <h2 className="text-lg font-semibold">
                    {show?.action === 'add' ? 'Create Council' : 'Update Council'}
                </h2>
                <p className="text-sm text-gray-500">
                    {show?.action === 'add' ? 'Fill in the details to create a new council.' : 'Update the council details as needed.'}
                </p>
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <TextInput
                    label="Council Name"
                    name="councilName"
                    defaultValue={show?.council?.councilName}
                    required
                    placeholder="Enter council name"
                    alwaysFocused
                />

                <TextInput
                    label="Council Code"
                    name="councilCode"
                    defaultValue={show?.council?.councilCode}
                    required
                    placeholder="Enter council code"
                    alwaysFocused
                />

                <Textarea
                    label="Address"
                    name="address"
                    defaultValue={show?.council?.address}
                    placeholder="Enter council address"
                />

                <TextInput
                    label="Telephone"
                    name="telephone"
                    defaultValue={show?.council?.telephone}
                    placeholder="Enter council telephone"
                    alwaysFocused
                />

                <TextInput
                    label="Website"
                    name="website"
                    defaultValue={show?.council?.website}
                    placeholder="Enter council website"
                    alwaysFocused
                />

                <TextInput
                    label="Emails (comma-separated)"
                    name="emails"
                    defaultValue={show?.council?.emails?.join(', ')}
                    placeholder="Enter council emails (comma-separated)"
                    alwaysFocused
                />

                <TextInput
                    label="Registrar Name"
                    name="registrarName"
                    defaultValue={show?.council?.registrarName || ""}
                    placeholder="Enter registrar name (max 255 characters)"
                    maxLength={255}
                    alwaysFocused
                />

                {
                    show?.action === 'edit' && show.council?.councilProfileDocumentPath && (
                        <div className='mb-4'>
                            <p className='text-sm text-gray-600'>Current Profile Document:</p>
                            <img
                                src={getCouncilProfileUrl(show.council.councilProfileDocumentPath)}
                                alt="Council Profile"
                                className='w-32 h-32 object-cover rounded-md mt-2'
                            />
                        </div>
                    )
                }
                <CustomFileInput
                    label="Council Profile Document"
                    name="councilProfileDocument"
                    accept="image/*"
                    files={newCouncilProfileDocument ? [newCouncilProfileDocument] : []}
                    onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        console.log('Selected file:', file);
                        setNewCouncilProfileDocument(file);
                        if (file) {
                            e.target.setCustomValidity(''); // Clear any previous validation message
                        } else {
                            e.target.setCustomValidity('Please upload a council profile document.');
                        }
                    }
                    }
                    required={show?.action === 'add'}
                    maxSize={5 * 1024 * 1024}
                />
                <div className='flex justify-end items-center gap-4 pt-4 border-t'>
                    <Button
                        variant='outline'
                        color='gray'
                        onClick={() => setShow(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        loading={creatingCouncil || editingCouncil}
                    >
                        {show?.action === 'add' ? 'Create Council' : 'Update Council'}
                    </Button>
                </div>
            </form>
        </ModalLayout>
    )
}
